import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Alert } from '../../components/ui'

/**
 * PDF 用印共用元件
 *
 * 「一般用印」與「合約用印」兩頁共用這一份；差別只有印章從哪來、誰能開這一頁，
 * 拖拉定位、座標換算、合成輸出的邏輯完全一樣。
 *
 * 整個流程都在瀏覽器完成，PDF 不上傳伺服器：
 *   選檔 → pdfjs 渲染預覽 → 拖拉印章 → pdf-lib 把印章寫進去 → 下載
 *
 * pdfjs 與 pdf-lib 都用動態 import 載入（合計約 500KB），
 * 不進主 bundle，其他頁面不受影響。
 */

/** PDF 的長度單位是 point（1/72 吋），跟公釐的換算比 */
const PT_PER_MM = 72 / 25.4

/** 預覽畫布的最大寬度（CSS px）。太寬會讓整頁塞不進視窗，拖拉時要一直捲動 */
const MAX_PREVIEW_W = 720

/**
 * 把「畫面上的位置」換算成「寫進 PDF 的位置」。
 *
 * 為什麼需要這一段：掃描歪掉的 PDF 會帶 /Rotate 屬性（90/180/270）。
 * pdfjs 預覽時會自動把頁面轉正，所以畫面上看到的是正的；但 pdf-lib 寫入時
 * 用的是「未旋轉的原始座標系」，兩者對不上——不處理的話，橫躺的掃描件
 * 拖到哪都蓋不到那裡。
 *
 * 座標系差異：
 *   畫面   原點在左上，y 往下增加，尺寸是「轉正後」的
 *   PDF    原點在左下，y 往上增加，尺寸是 mediaBox 的原始值
 *
 * rot 是印章自己要轉的角度：頁面被轉正了幾度來顯示，印章就要反向轉回去，
 * 這樣印在原始頁面上、經過檢視器套用 /Rotate 之後才會是正的。
 *
 * @param sx,sy  印章左上角在畫面座標的位置（pt）
 * @param sw,sh  印章在畫面座標的寬高（pt）
 * @param W,H    mediaBox 的原始寬高（pt）
 * @param rotation 頁面的 /Rotate 值
 */
function toPdfSpace(
  sx: number, sy: number, sw: number, sh: number,
  W: number, H: number, rotation: number,
) {
  const R = ((rotation % 360) + 360) % 360
  /* 注意：pdf-lib 的 rotate 是「繞 (x,y) 這個左下角」轉，轉完影像會落到錨點的
     另一側，所以下面每個角度的 x/y 都已經把那段位移補回去了。
     這四組值是實測驗證過的，不要憑直覺改。 */
  switch (R) {
    /* 畫面往右 = PDF 往上；畫面往下 = PDF 往右 */
    case 90:
      return { x: sy, y: sx + sw, rot: 270, w: sw, h: sh }
    /* 整頁翻轉：畫面右下角才是 mediaBox 的原點方向 */
    case 180:
      return { x: W - sx, y: sy + sh, rot: 180, w: sw, h: sh }
    /* 畫面左上對應 mediaBox 右上 */
    case 270:
      return { x: W - sy, y: H - sx - sw, rot: 90, w: sw, h: sh }
    /* 沒旋轉：只要把 y 從「由上往下」翻成「由下往上」 */
    default:
      return { x: sx, y: H - sy - sh, rot: 0, w: sw, h: sh }
  }
}

type StampPos = {
  /** 這一枚印章的識別碼，只在前端用來分辨拖的是哪一枚 */
  id: number
  /**
   * 印章放在第幾頁（1 起算）。
   * 印章綁定頁碼而不是浮在所有頁面上——同一份 PDF 可能多頁，要蓋的頁數不固定，
   * 沒有綁頁的話翻頁時印章跟著跑，會分不清到底蓋在哪一頁。
   */
  page: number
  /** 印章左上角在頁面上的位置（mm，從頁面左上角算起） */
  xMm: number
  yMm: number
  /** 印章寬度（mm），高度依原圖比例自動算 */
  wMm: number
}

type Props = {
  /** 頁面標題與副標。由這裡渲染 page-head，下載按鈕才能放在標題列右邊 */
  title: string
  hint?: string
  /** 標題列下方的提示訊息（各頁自己的錯誤／警告） */
  alerts?: ReactNode
  /** 印章圖來源：站內路徑或 dataURL。null 代表還沒有章可用 */
  sealSrc: string | null
  /** 沒有印章時要顯示的說明 */
  sealHint?: ReactNode
  /** 印章預設寬度（mm） */
  defaultWidthMm?: number
  /**
   * 記住上次位置與大小用的 localStorage key。
   * 同一種文件的用印位置通常固定，記住可以省去每次重新對位。
   */
  rememberKey?: string
  /** 印章面板上方要插入的內容（一般用印用來放上傳區） */
  sidebarTop?: ReactNode
}

export default function PdfStamper({
  title,
  hint,
  alerts,
  sealSrc,
  sealHint,
  defaultWidthMm = 50,
  rememberKey,
  sidebarTop,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  /** pdf-lib 要用的原始位元組。pdfjs 會接管（detach）傳進去的 buffer，所以另外留一份副本 */
  const bytesRef = useRef<Uint8Array | null>(null)
  const docRef = useRef<any>(null)
  /** 進行中的渲染工作。連續翻頁時要先取消上一個，否則 pdfjs 會拒絕在同一個 canvas 上重複渲染 */
  const renderTaskRef = useRef<any>(null)
  /** 拖拉時滑鼠與印章左上角的距離（mm），避免抓著章的中間卻跳到滑鼠位置 */
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null)

  /** 待處理的檔案佇列。一次只開一份，確認後下載再換下一份 */
  const [queue, setQueue] = useState<File[]>([])
  const [queueIdx, setQueueIdx] = useState(0)
  const [fileName, setFileName] = useState('')
  const [pageNum, setPageNum] = useState(1)
  /**
   * 頁碼輸入框的暫存值，跟 pageNum 分開。
   * 直接綁 pageNum 的話，使用者刪掉數字準備重打的那一瞬間就會被當成無效輸入，
   * 打「12」也會在只輸入「1」時先跳到第 1 頁。改成打完按 Enter 或離開欄位才跳。
   */
  const [pageInput, setPageInput] = useState('1')
  const [numPages, setNumPages] = useState(0)
  /** 目前頁面的實際尺寸（mm）與旋轉角度 */
  const [pageMm, setPageMm] = useState({ w: 210, h: 297 })
  const [rotation, setRotation] = useState(0)
  /** 預覽畫布上 1mm 等於幾個 CSS px */
  const [pxPerMm, setPxPerMm] = useState(1)
  /**
   * 同一份 PDF 可以蓋很多枚章：多頁合約常常每頁都要用印，同一頁也可能蓋兩處
   * （例如騎縫與簽名欄）。所以存成陣列，翻頁只是換顯示哪一頁的章，不會把別頁的清掉。
   */
  const [stamps, setStamps] = useState<StampPos[]>([])
  /** 目前選取的那一枚：大小、位置、方向鍵微調與刪除都作用在它身上 */
  const [activeId, setActiveId] = useState<number | null>(null)
  const nextIdRef = useRef(1)
  /** 印章原圖的長寬比（高 ÷ 寬），用來由寬度推算高度 */
  const [sealRatio, setSealRatio] = useState(1)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  /* 讀印章的原始比例。印章換了（一般用印重新上傳）就要重算 */
  useEffect(() => {
    if (!sealSrc) return
    const img = new Image()
    img.onload = () => setSealRatio(img.naturalHeight / img.naturalWidth)
    img.src = sealSrc
  }, [sealSrc])

  /*
   * 頁面尺寸或印章比例一變，就把印章夾回紙面內。
   * 這不只是保險：記住的位置是上一份直式文件的（例如 Y=200mm），換到橫式頁面
   * （高只有 210mm）就會整個掉到紙外——夾住邊界的邏輯只在拖拉時跑，換頁不會。
   */
  useEffect(() => {
    setStamps((list) => {
      let changed = false
      const next = list.map((s) => {
        /* 只夾這一頁的章。別頁的尺寸不是現在的 pageMm，拿去夾會把座標改掉，
           翻回去就跑位了 */
        if (s.page !== pageNum) return s
        const hMm = s.wMm * sealRatio
        const x = Math.min(s.xMm, Math.max(0, pageMm.w - s.wMm))
        const y = Math.min(s.yMm, Math.max(0, pageMm.h - hMm))
        if (x === s.xMm && y === s.yMm) return s
        changed = true
        return { ...s, xMm: x, yMm: y }
      })
      return changed ? next : list
    })
  }, [pageMm, sealRatio, pageNum])

  /** 把印章放到目前這一頁。位置沿用上次記住的，沒有就用預設 */
  const placeStamp = useCallback(() => {
    let pos = { xMm: 120, yMm: 200, wMm: defaultWidthMm }
    if (rememberKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(rememberKey) || 'null')
        if (saved && typeof saved.xMm === 'number') {
          pos = { xMm: saved.xMm, yMm: saved.yMm, wMm: saved.wMm ?? defaultWidthMm }
        }
      } catch {
        /* 存壞了就用預設，不要讓整頁掛掉 */
      }
    }
    /* 記住的位置可能來自比較大的頁面，先夾回這一頁的範圍內 */
    const hMm = pos.wMm * sealRatio
    const id = nextIdRef.current++
    setStamps((list) => {
      let xMm = Math.min(pos.xMm, Math.max(0, pageMm.w - pos.wMm))
      let yMm = Math.min(pos.yMm, Math.max(0, pageMm.h - hMm))
      /* 同一頁再蓋一枚時往右下偏一點，兩枚完全疊在一起會看不出多蓋了一枚 */
      while (list.some((s) => s.page === pageNum && Math.abs(s.xMm - xMm) < 1 && Math.abs(s.yMm - yMm) < 1)) {
        xMm = Math.min(xMm + 6, Math.max(0, pageMm.w - pos.wMm))
        yMm = Math.min(yMm + 6, Math.max(0, pageMm.h - hMm))
      }
      return [...list, { id, page: pageNum, wMm: pos.wMm, xMm, yMm }]
    })
    setActiveId(id)
  }, [rememberKey, defaultWidthMm, pageNum, pageMm, sealRatio])

  const renderPage = useCallback(async (n: number) => {
    const doc = docRef.current
    const canvas = canvasRef.current
    if (!doc || !canvas) return
    const page = await doc.getPage(n)

    /* scale 1 時 viewport 的單位就是 point，先量出實際紙張大小 */
    const base = page.getViewport({ scale: 1 })
    const wMm = base.width / PT_PER_MM
    const hMm = base.height / PT_PER_MM
    setPageMm({ w: wMm, h: hMm })
    setRotation(page.rotate || 0)

    /* 依容器寬度決定顯示比例，讓整頁看得到又不會太小 */
    const stageW = Math.min(stageRef.current?.clientWidth || MAX_PREVIEW_W, MAX_PREVIEW_W)
    const scale = stageW / base.width
    const viewport = page.getViewport({ scale })

    /* 高解析螢幕要用實際像素密度畫，不然預覽會糊，看不出章壓到哪 */
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.floor(viewport.width * dpr)
    canvas.height = Math.floor(viewport.height * dpr)
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    const ctx = canvas.getContext('2d')!
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, viewport.width, viewport.height)

    /* 連按翻頁時上一次的渲染可能還沒跑完。pdfjs 不允許同一個 canvas 同時被兩個
       render 佔用，會直接丟錯，所以先取消前一個再開始。 */
    renderTaskRef.current?.cancel()
    const task = page.render({ canvas, canvasContext: ctx, viewport })
    renderTaskRef.current = task
    try {
      await task.promise
    } catch (e: any) {
      /* 被自己取消是正常流程，不是錯誤；其他狀況才往外拋 */
      if (e?.name !== 'RenderingCancelledException') throw e
      return
    }

    setPxPerMm(viewport.width / wMm)
  }, [])

  /** 選檔：可一次選多份，排成佇列後從第一份開始 */
  const onFiles = async (list: FileList | null) => {
    const files = Array.from(list ?? [])
    if (!files.length) return
    setQueue(files)
    setQueueIdx(0)
    await loadFile(files[0])
  }

  const loadFile = async (file: File | undefined) => {
    if (!file) return
    setError('')
    setBusy('讀取 PDF…')
    try {
      const buf = new Uint8Array(await file.arrayBuffer())
      bytesRef.current = buf.slice()          // pdfjs 會接管原本那份，這裡先複製給 pdf-lib

      const pdfjs = await import('pdfjs-dist')
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

      const doc = await pdfjs.getDocument({ data: buf }).promise
      docRef.current = doc
      setNumPages(doc.numPages)
      setPageNum(1)
      setPageInput('1')     // 輸入框也要一起回到第 1 頁，否則會留著上一份的頁碼
      setFileName(file.name)
      /* 不自動放印章：要蓋哪一頁、蓋在哪由使用者翻到該頁後自己按 */
      setStamps([])
      setActiveId(null)
      await renderPage(1)
    } catch (e: any) {
      setError(`這個檔案讀不起來：${e?.message || e}`)
      docRef.current = null
      setNumPages(0)
    } finally {
      setBusy('')
    }
  }

  const goPage = async (n: number) => {
    if (n < 1 || n > numPages) return
    setPageNum(n)
    setPageInput(String(n))
    /* 選取跟著換到這一頁的章：大小與位置面板才會對應到看得到的那一枚 */
    setActiveId(stamps.find((s) => s.page === n)?.id ?? null)
    await renderPage(n)
  }

  /** 送出頁碼輸入。數字不合法或超出範圍就退回目前頁碼，不要讓欄位停在無效狀態 */
  const commitPageInput = () => {
    const n = Number.parseInt(pageInput, 10)
    if (!Number.isFinite(n) || n < 1 || n > numPages) {
      setPageInput(String(pageNum))
      return
    }
    if (n !== pageNum) goPage(n)
  }

  /* ---- 拖拉 ---- */

  /*
   * 監聽掛在 window 而不是印章本身。
   * 印章只有幾十像素見方，滑鼠稍微拖快一點就跑到圖外，元素上的 pointermove
   * 就收不到了；setPointerCapture 理論上能解決，但合成事件與部分裝置上會失效。
   * 掛 window 最單純可靠，放開時再解除。
   */
  const onStampDown = (e: React.PointerEvent, target: StampPos) => {
    e.preventDefault()
    setActiveId(target.id)        // 拖哪一枚就選哪一枚
    pageRef.current?.focus()      // 讓方向鍵微調立刻可用，不必再點一次
    const rect = canvasRef.current!.getBoundingClientRect()
    dragOffset.current = {
      dx: (e.clientX - rect.left) / pxPerMm - target.xMm,
      dy: (e.clientY - rect.top) / pxPerMm - target.yMm,
    }

    const move = (ev: PointerEvent) => {
      const off = dragOffset.current
      if (!off) return
      const r = canvasRef.current!.getBoundingClientRect()
      /* 用函式式更新：這個 handler 的閉包抓到的是按下去當下的位置，
         連續拖曳時會一直拿到舊值 */
      setStamps((list) => list.map((s) => {
        if (s.id !== target.id) return s
        const hMm = s.wMm * sealRatio
        /* 夾在頁面範圍內：整個章跑到紙外就等於沒蓋到 */
        return {
          ...s,
          xMm: Math.min(Math.max(0, (ev.clientX - r.left) / pxPerMm - off.dx), pageMm.w - s.wMm),
          yMm: Math.min(Math.max(0, (ev.clientY - r.top) / pxPerMm - off.dy), pageMm.h - hMm),
        }
      }))
    }
    const up = () => {
      dragOffset.current = null
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      rememberRef.current()
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const remember = () => {
    if (!rememberKey || !active) return
    /* 只記位置與大小，id 與頁碼是這一份檔案專屬的，記了反而會誤導下一份 */
    const { xMm, yMm, wMm } = active
    localStorage.setItem(rememberKey, JSON.stringify({ xMm, yMm, wMm }))
  }
  /* 拖曳結束時要存的是「最新」的位置，但 up 是在 pointerdown 當下建立的閉包，
     直接呼叫 remember 會存到舊值。用 ref 永遠指向最新一份 */
  const rememberRef = useRef(remember)
  rememberRef.current = remember

  /* 方向鍵微調：純滑鼠很難對準對方預留的框線 */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!active) return
    const step = e.shiftKey ? 5 : 0.5
    const map: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
    }
    const d = map[e.key]
    if (!d) return
    e.preventDefault()
    const hMm = active.wMm * sealRatio
    setStamps((list) => list.map((s) => (s.id !== active.id ? s : {
      ...s,
      xMm: Math.min(Math.max(0, s.xMm + d[0]), pageMm.w - s.wMm),
      yMm: Math.min(Math.max(0, s.yMm + d[1]), pageMm.h - hMm),
    })))
  }

  const setWidth = (wMm: number) => {
    if (!active) return
    const hMm = wMm * sealRatio
    setStamps((list) => list.map((s) => (s.id !== active.id ? s : {
      ...s,
      xMm: Math.min(s.xMm, pageMm.w - wMm),
      yMm: Math.min(s.yMm, pageMm.h - hMm),
      wMm,
    })))
  }

  /** 刪掉選取的那一枚；選取自動落到同一頁的下一枚，沒有就清空 */
  const removeActive = () => {
    if (!active) return
    const rest = stamps.filter((s) => s.id !== active.id)
    setStamps(rest)
    setActiveId(rest.find((s) => s.page === pageNum)?.id ?? null)
  }

  /* ---- 輸出 ---- */

  const download = async () => {
    if (!bytesRef.current || stamps.length === 0 || !sealSrc) return
    setError('')
    setBusy('產生 PDF…')
    try {
      const { PDFDocument, degrees } = await import('pdf-lib')
      const pdf = await PDFDocument.load(bytesRef.current)

      const sealBytes = new Uint8Array(await (await fetch(sealSrc)).arrayBuffer())
      const png = await pdf.embedPng(sealBytes)

      /* 一份 PDF 可能蓋了好幾枚，逐枚寫進它自己那一頁 */
      const pages = pdf.getPages()
      for (const st of stamps) {
        const page = pages[st.page - 1]
        if (!page) continue
        const wPt = st.wMm * PT_PER_MM
        const hPt = wPt * sealRatio
        const { width: W, height: H } = page.getSize()
        const t = toPdfSpace(
          st.xMm * PT_PER_MM, st.yMm * PT_PER_MM, wPt, hPt,
          W, H, page.getRotation().angle,
        )
        page.drawImage(png, { x: t.x, y: t.y, width: t.w, height: t.h, rotate: degrees(t.rot) })
      }

      const out = await pdf.save()
      const blob = new Blob([out as BlobPart], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName.replace(/\.pdf$/i, '') + '_已用印.pdf'
      a.click()
      URL.revokeObjectURL(url)
      remember()

      /* 佇列還有下一份就自動接上。位置與大小沿用剛才這一份——
         批量處理的檔案通常格式一致，不必每份重新對位 */
      if (queueIdx + 1 < queue.length) {
        setQueueIdx(queueIdx + 1)
        await loadFile(queue[queueIdx + 1])
      }
    } catch (e: any) {
      setError(`產生失敗：${e?.message || e}`)
    } finally {
      setBusy('')
    }
  }

  const active = stamps.find((s) => s.id === activeId) ?? null
  const pageStamps = stamps.filter((s) => s.page === pageNum)
  const hMm = active ? active.wMm * sealRatio : 0
  const hasNext = queueIdx + 1 < queue.length
  /* 已蓋章的頁碼，用來提醒別頁也有章（翻頁後看不到，不講會以為不見了） */
  const stampedPages = [...new Set(stamps.map((s) => s.page))].sort((a, b) => a - b)

  return (
    <>
      {/* 下載是這一頁的最終動作，放在標題列右邊——側欄那一長串是「調整」，
          把「完成」混在裡面不容易找 */}
      <div className="page-head">
        <h2>{title}</h2>
        {hint && <span className="hint">{hint}</span>}
        <div className="spacer" />
        {numPages > 0 && (
          <button className="primary" onClick={download} disabled={!sealSrc || stamps.length === 0 || !!busy}>
            {hasNext ? '下載並處理下一份' : '下載已用印 PDF'}
          </button>
        )}
      </div>
      {alerts}

      <div className="stamper">
      <div className="stamper-panel">
        {sidebarTop}

        <div className="sp-block">
          <span className="sp-label">合約 PDF</span>
          {queue.length > 1 && (
            <div className="sp-queue">第 {queueIdx + 1} / {queue.length} 份</div>
          )}
          {fileName ? <div className="sp-file">{fileName}</div> : <div className="sp-dim">尚未選擇</div>}
          <label className="sp-filebtn">
            {fileName ? '重新選擇' : '選擇 PDF'}
            <input
              type="file"
              accept="application/pdf"
              multiple
              onChange={(e) => onFiles(e.target.files)}
              style={{ display: 'none' }}
            />
          </label>
          {!fileName && <div className="sp-dim" style={{ marginTop: 6 }}>可一次選多份</div>}
        </div>

        {numPages > 0 && (
          <>
            <div className="sp-block">
              <span className="sp-label">頁面</span>
              <div className="sp-pager">
                <button onClick={() => goPage(pageNum - 1)} disabled={pageNum <= 1}>‹</button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value.replace(/[^\d]/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPageInput() } }}
                  onBlur={commitPageInput}
                  /* 點進來就整個選起來，直接打新頁碼不用先刪。
                     要等這一輪 render 結束再選，否則受控欄位重繪會把游標打回去；
                     mouseup 預設會重設選取範圍，所以一併擋掉 */
                  onFocus={(e) => { const el = e.currentTarget; requestAnimationFrame(() => el.select()) }}
                  onMouseUp={(e) => e.preventDefault()}
                  aria-label="頁碼"
                />
                <span className="sp-pagetotal">之 {numPages}</span>
                <button onClick={() => goPage(pageNum + 1)} disabled={pageNum >= numPages}>›</button>
              </div>
              {/* 用印與收回跟翻頁放在一起：要蓋哪一頁是翻到那一頁才決定的，
                  兩個動作分在不同區塊會讓人以為印章跟頁碼無關 */}
              <div className="sp-row">
                {/* 同一頁可以連按，一份 PDF 想蓋幾枚就蓋幾枚 */}
                <button onClick={placeStamp} disabled={!sealSrc}>
                  用印
                </button>
                <button onClick={removeActive} disabled={!active}>
                  刪除
                </button>
              </div>
              {stamps.length > 0 && (
                /* 別頁的章翻過去才看得到，不講會以為被蓋掉了 */
                <div className="sp-dim" style={{ marginTop: 6 }}>
                  已用印 {stamps.length} 枚：第 {stampedPages.join('、')} 頁
                  {pageStamps.length > 1 && `（本頁 ${pageStamps.length} 枚）`}
                </div>
              )}
            </div>

            {active && (
              <>
                <div className="sp-block">
                  <span className="sp-label">
                    印章大小{pageStamps.length > 1 ? '（選取的那一枚）' : ''}
                  </span>
                  <input
                    type="range" min={15} max={90} step={1}
                    value={active.wMm}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    onMouseUp={remember}
                    style={{ width: '100%' }}
                  />
                  <div className="sp-num">{active.wMm.toFixed(0)} × {hMm.toFixed(0)} mm</div>
                </div>

                <div className="sp-block">
                  <span className="sp-label">位置</span>
                  <div className="sp-num">
                    X {active.xMm.toFixed(1)} mm<br />
                    Y {active.yMm.toFixed(1)} mm
                  </div>
                </div>
              </>
            )}

          </>
        )}

        {busy && <div className="sp-dim">{busy}</div>}
      </div>

      <div className="stamper-stage" ref={stageRef}>
        {error && <Alert kind="error">{error}</Alert>}
        {!sealSrc && sealHint}
        {rotation !== 0 && (
          <Alert kind="ok">
            這一頁帶有 {rotation}° 旋轉。預覽顯示的就是檢視器實際會呈現的方向，
            印章也會照這個方向蓋——你看到在哪，印出來就在哪。
            下載的檔案維持原本的頁面方向，只多了印章。
          </Alert>
        )}
        {numPages === 0 && !error && <div className="sp-drop">選擇一份 PDF 開始</div>}

        <div
          className="stamper-page"
          ref={pageRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          style={{ display: numPages > 0 ? 'inline-block' : 'none' }}
        >
          <canvas ref={canvasRef} />
          {/* 只畫這一頁的章；別頁的留在狀態裡，翻回去就看得到 */}
          {sealSrc && pageStamps.map((st) => (
            <img
              key={st.id}
              className={`stamper-seal${st.id === activeId ? ' active' : ''}`}
              src={sealSrc}
              alt="印章"
              draggable={false}
              onPointerDown={(e) => onStampDown(e, st)}
              style={{
                left: st.xMm * pxPerMm,
                top: st.yMm * pxPerMm,
                width: st.wMm * pxPerMm,
              }}
            />
          ))}
        </div>

        {numPages > 0 && (
          <p className="sp-tip">拖曳移動．方向鍵微調 0.5mm．Shift + 方向鍵 5mm</p>
        )}
      </div>
      </div>
    </>
  )
}
